import React, { useState, useEffect, useCallback } from 'react';
import './GalleryModal.css';
import { useLanguage } from '../context/LanguageContext';

const GalleryModal = ({ isOpen, currentImageIndex, images, imageUrls = [], onClose, onPrevious, onNext }) => {
  const { t } = useLanguage();
  const [slideDirection, setSlideDirection] = useState(null);
  
  // Helper function to check if item is a video
  const isVideo = (src) => {
    return src && (src.endsWith('.mp4') || src.endsWith('.webm') || src.endsWith('.ogg'));
  };
  
  // Helper function to detect mobile devices
  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) 
      || window.innerWidth <= 768;
  };
  
  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!isOpen) return;
    
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowLeft') {
      setSlideDirection('left');
      onPrevious();
    } else if (e.key === 'ArrowRight') {
      setSlideDirection('right');
      onNext();
    }
  }, [isOpen, onClose, onPrevious, onNext]);
  
  // Setup keyboard event listeners
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);
  
  // Handle swipe gestures for mobile
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  
  // Minimum swipe distance required (in px)
  const minSwipeDistance = 50;
  
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  
  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      setSlideDirection('right');
      onNext();
    } else if (isRightSwipe) {
      setSlideDirection('left');
      onPrevious();
    }
  };
  
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);
  
  // Handle slide transition effect
  const getSlideClass = () => {
    if (!slideDirection) return 'active';
    
    if (slideDirection === 'right') {
      return 'slide-in-right';
    } else if (slideDirection === 'left') {
      return 'slide-in-left';
    }
    
    return '';
  };
  
  // Check if the current image has a URL
  const getCurrentImageUrl = () => {
    return imageUrls && imageUrls.length > currentImageIndex ? imageUrls[currentImageIndex] : null;
  };
  
  // Render media (image or video) with or without a link based on URL availability
  const renderMedia = () => {
    const imageUrl = getCurrentImageUrl();
    const currentItem = images[currentImageIndex];
    
    const mediaElement = isVideo(currentItem) ? (
      <video 
        src={currentItem} 
        className={`gallery-modal-image ${getSlideClass()}`}
        controls
        autoPlay={!isMobile()}
        muted
        loop
        preload="metadata"
        playsInline
      />
    ) : (
      <img 
        src={currentItem} 
        alt={`Tatuaj Kellink Tattoo Bacău ${currentImageIndex + 1}`} 
        className={`gallery-modal-image ${getSlideClass()}`} 
      />
    );
    
    if (imageUrl) {
      return (
        <a 
          href={imageUrl} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="gallery-modal-link"
          onClick={(e) => e.stopPropagation()}
          title={t('gallery.visitLink')}
        >
          {mediaElement}
          <div className="gallery-modal-link-indicator" title={t('gallery.visitLink')}>
            <i className="fas fa-external-link-alt"></i>
          </div>
        </a>
      );
    }
    
    return mediaElement;
  };
  
  return (
    <div className={`gallery-modal-overlay ${isOpen ? 'active' : ''}`}>
      <div 
        className="gallery-modal-container"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div className="gallery-modal-image-container">
          {isOpen && images.length > 0 && renderMedia()}
          
          <div className="gallery-modal-navigation">
            <button 
              className="gallery-modal-prev" 
              onClick={() => {
                setSlideDirection('left');
                onPrevious();
              }}
              aria-label={t('gallery.navigationPrev')}
            >
              <i className="fas fa-chevron-left"></i>
            </button>
            <button 
              className="gallery-modal-next" 
              onClick={() => {
                setSlideDirection('right');
                onNext();
              }}
              aria-label={t('gallery.navigationNext')}
            >
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
          
          <div className="modal-gallery-counter">
            {currentImageIndex + 1} / {images.length}
          </div>
        </div>
        
        <button 
          className="gallery-modal-close" 
          onClick={onClose}
          aria-label={t('gallery.closeModal')}
        >
          <i className="fas fa-times"></i>
        </button>
        
       
      </div>
    </div>
  );
};

export default GalleryModal; 